import { Meta } from './segments';
import { termsOfServiceMeta } from '@/utils';

const TermsOfService = () => {
  return (
    <>
      <Meta {...termsOfServiceMeta} />

      <main className="container py-5">
        <h1 className="mb-2">Terms of Service</h1>
        <p className="text-muted mb-4">Last updated: September 30, 2025</p>

        <section className="mb-4">
          <h2 className="mb-3">1. Introduction</h2>
          <p>
            Welcome to Convert Playlist ("we," "our," or "us"). By accessing or
            using our website at{' '}
            <a
              href="https://convertplaylist.net"
              className="text-decoration-none"
            >
              convertplaylist.net
            </a>{' '}
            and our playlist conversion service (collectively, the "Service"),
            you agree to be bound by these Terms of Service ("Terms").
          </p>
          <p>
            Please read these Terms carefully. If you do not agree with these
            Terms, you should not use our Service.
          </p>
          <p>
            <strong>YouTube Terms of Service.</strong> By using Convert Playlist
            (including any features that access or use YouTube API Services),
            you agree to be bound by the{' '}
            <a
              href="https://www.youtube.com/t/terms"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              YouTube Terms of Service
            </a>
            .
          </p>
          <p>
            <strong>Spotify Terms of Use.</strong> By using Convert Playlist
            with Spotify (including features powered by the Spotify Web API),
            you agree to be bound by the{' '}
            <a
              href="https://www.spotify.com/legal/end-user-agreement/"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Spotify Terms of Use
            </a>
            .
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">2. Eligibility</h2>
          <p>
            You must be at least 13 years old to use our Service. By using our
            Service, you represent and warrant that you meet this requirement.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">3. Your Account</h2>
          <p>
            To use our Service, you may need to create an account. You are
            responsible for maintaining the confidentiality of your account
            credentials and for all activities that occur under your account.
            You agree to notify us immediately of any unauthorized use of your
            account.
          </p>
          <p>
            We use a passwordless authentication system that sends one-time
            passwords (OTPs) to your email address. You are responsible for
            ensuring that your email address is secure and accessible.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">4. Service Description</h2>
          <p>
            Convert Playlist is a service that allows users to convert music
            playlists between different streaming platforms. We currently
            support conversions between Spotify, Apple Music, and YouTube Music,
            with more platforms planned for the future.
          </p>
          <p>
            Our Service uses the official APIs of music streaming platforms to
            read your playlists and create new ones on your destination
            platform. We use matching algorithms to find the same songs across
            different platforms.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">5. Free Service and Future Premium Features</h2>
          <p>
            We currently offer our Service for free with certain limitations. We
            may introduce premium features in the future that will be available
            for a subscription fee. Any such premium features will be clearly
            marked, and you will have the option to continue using the free
            service or upgrade to a premium subscription.
          </p>
          <p>
            If we introduce premium features, we will update these Terms to
            include information about subscription fees, billing, and
            cancellation policies.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">6. User Content</h2>
          <p>
            Our Service allows you to convert playlists that you have access to
            on supported music streaming platforms. You represent and warrant
            that you have the necessary rights to convert such playlists and
            that your use of our Service does not violate any third-party
            rights.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">7. Intellectual Property</h2>
          <p>
            All content, features, and functionality of our Service, including
            but not limited to text, graphics, logos, icons, and software, are
            owned by us or our licensors and are protected by copyright,
            trademark, and other intellectual property laws.
          </p>
          <p>
            We do not claim ownership of the music or playlists that you convert
            using our Service. The rights to this content remain with the
            original copyright holders.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">8. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li className="mb-2">
              Use our Service for any illegal purpose or in violation of any
              local, state, national, or international law
            </li>
            <li className="mb-2">
              Violate or encourage others to violate the rights of third
              parties, including intellectual property rights
            </li>
            <li className="mb-2">
              Attempt to circumvent any security features of our Service
            </li>
            <li className="mb-2">
              Use automated scripts to access or interact with our Service
            </li>
            <li className="mb-2">
              Engage in any activity that interferes with or disrupts our
              Service
            </li>
            <li className="mb-2">
              Impersonate any person or entity or falsely state or otherwise
              misrepresent your affiliation with a person or entity
            </li>
          </ul>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">9. Third-Party Services</h2>
          <p>
            Our Service integrates with third-party music streaming platforms.
            Your use of these platforms is subject to their respective terms of
            service and privacy policies. We are not responsible for the
            content, privacy practices, or other policies of these third-party
            platforms.
          </p>
          <p>
            <strong>Use of YouTube API Services.</strong> Parts of the Service
            are powered by YouTube API Services. By using the Service (including
            features powered by YouTube API Services), you agree to be bound by
            the{' '}
            <a
              href="https://www.youtube.com/t/terms"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              YouTube Terms of Service
            </a>
            .
          </p>
          <p>
            <strong>Use of Spotify API Services.</strong> Parts of the Service
            are powered by the Spotify Web API. By using the Service with
            Spotify, you agree to be bound by the{' '}
            <a
              href="https://www.spotify.com/legal/end-user-agreement/"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Spotify Terms of Use
            </a>
            .
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">10. Disclaimer of Warranties</h2>
          <p className="text-uppercase">
            OUR SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT
            LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </p>
          <p>
            We do not guarantee that our Service will always be available,
            uninterrupted, or error-free. We may occasionally need to perform
            maintenance, which may temporarily suspend access to our Service.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">11. Limitation of Liability</h2>
          <p className="text-uppercase">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS,
            DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR
            ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Convert Playlist
            and its officers, directors, employees, agents, and affiliates from
            and against any and all claims, damages, obligations, losses,
            liabilities, costs, or debt, and expenses (including but not limited
            to attorney's fees) arising from: (i) your use of and access to the
            Service; (ii) your violation of any term of these Terms; (iii) your
            violation of any third-party right, including without limitation any
            copyright, property, or privacy right; or (iv) any claim that your
            use of the Service caused damage to a third party.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">13. Modifications to the Service and Terms</h2>
          <p>
            We reserve the right to modify or discontinue, temporarily or
            permanently, the Service (or any part thereof) with or without
            notice. We shall not be liable to you or to any third party for any
            modification, suspension, or discontinuance of the Service.
          </p>
          <p>
            We may revise these Terms from time to time. The most current
            version will always be posted on our website. By continuing to
            access or use our Service after revisions become effective, you
            agree to be bound by the revised Terms.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">14. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service
            immediately, without prior notice or liability, for any reason
            whatsoever, including without limitation if you breach these Terms.
          </p>
          <p>
            Upon termination, your right to use the Service will immediately
            cease. If you wish to terminate your account, you may simply
            discontinue using the Service.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">15. Governing Law</h2>
          <p>
            These Terms shall be governed and construed in accordance with the
            laws of the jurisdiction in which we operate, without regard to its
            conflict of law provisions.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">16. Contact Information</h2>
          <p>
            If you have any questions about these Terms, please contact us at{' '}
            <a
              href="mailto:contact@convertplaylist.net"
              className="text-decoration-none"
            >
              contact@convertplaylist.net
            </a>
            .
          </p>
        </section>
      </main>
    </>
  );
};

export default TermsOfService;
